import {
  JobFilterSelectableEntities,
  JobListCustomerListEntities,
  JobListCustomerResponseDto,
  JobListPaginatedCustomerResponseDtoWithEntities,
  JobListPaginatedCustomerResponseDtoWithListEntities,
} from "@/lib/interfaces/customers/jobs";
import { Pagination } from "@/lib/interfaces/utils";
import { Dispatch, SetStateAction, useCallback, useEffect, useState } from "react";
import useApiRequest from "../../../lib/hooks/useRequest";
import DataFetchParent from "../../../ui/components/ui/data-fetch";
import { CustomerDataTable } from "./data-table";

const CustomerJobFetchParent = ({
  searchQuery,
  setSelectableEntities,
  resetPagination,
  setResetPagination,
}: {
  searchQuery: string;
  setSelectableEntities: Dispatch<SetStateAction<JobFilterSelectableEntities | undefined>>;
  resetPagination: boolean;
  setResetPagination: Dispatch<SetStateAction<boolean>>;
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [initLoading, setInitLoading] = useState(true);
  const [customers, setCustomers] = useState<JobListCustomerResponseDto[]>();
  const [reload, setReload] = useState(false);
  const [pageCount, setPageCount] = useState(0);
  const [pagination, setPagination] = useState<Pagination>({
    pageIndex: searchQuery.split("page=")[1] ? parseInt(searchQuery.split("page=")[1].split("&")[0]) - 1 : 0,
    pageSize: 10,
  });
  const [listEntities, setListEntities] = useState<JobListCustomerListEntities>();

  const { apiRequest } = useApiRequest();

  const getJobsInit = useCallback(async () => {
    setInitLoading(true);
    let url = `jobs/customer/initial?limit=${pagination.pageSize}`;
    if (searchQuery) {
      url += `&${searchQuery}`;
    }
    const res = await apiRequest<JobListPaginatedCustomerResponseDtoWithEntities>(url, "GET");

    setInitLoading(false);
    if (res.data) {
      setListEntities(res.data.listEntities);
      setCustomers(res.data.jobs.docs);
      setPageCount(Math.ceil(res.data.jobs.count / pagination.pageSize));
      setPageCount(res.data.jobs.pageCount);
      setSelectableEntities(res.data.filterSelectableEntities);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest, pagination.pageSize, setSelectableEntities]);

  const getJobsPaginated = useCallback(async () => {
    let url = `jobs/customer?limit=${pagination.pageSize}`;
    if (searchQuery) {
      url += `&${searchQuery}`;
    }

    const pageIndex = searchQuery.split("page=")[1] ? parseInt(searchQuery.split("page=")[1].split("&")[0]) - 1 : 0;
    if (pageIndex !== pagination.pageIndex) {
      setPagination({
        ...pagination,
        pageIndex: pageIndex,
      });
    }
    setIsLoading(true);
    const res = await apiRequest<JobListPaginatedCustomerResponseDtoWithListEntities>(url, "GET");

    setIsLoading(false);
    if (res.data) {
      setCustomers(res.data.jobs.docs);
      setPageCount(Math.ceil(res.data.jobs.count / pagination.pageSize));
      setPageCount(res.data.jobs.pageCount);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [apiRequest, pagination.pageSize, searchQuery]);

  useEffect(() => {
    getJobsInit();
  }, [getJobsInit]);

  useEffect(() => {
    if (!initLoading) {
      getJobsPaginated();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pagination.pageSize, getJobsPaginated, searchQuery, reload]);

  useEffect(() => {
    if (resetPagination) {
      setPagination({ pageIndex: 0, pageSize: 10 });
      setResetPagination(false);
    }
  }, [resetPagination, setResetPagination]);

  return (
    <>
      <DataFetchParent
        isLoading={initLoading}
        data={listEntities}
        renderElement={(listEntities) => (
          <CustomerDataTable
            data={customers ?? []}
            setReload={setReload}
            setPagination={setPagination}
            pageCount={pageCount}
            pagination={pagination}
            isLoading={isLoading}
            currentQuery={searchQuery}
            listEntities={listEntities}
          />
        )}
      />
    </>
  );
};

export default CustomerJobFetchParent;
